import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { endoReportService } from '../../services/endoReportService';
import { practitionerService } from '../../services/practitionerService';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import toast from 'react-hot-toast';
import { generateReferralPDF } from '../../utils/pdfGenerator';

const ReportEditorPreview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reportId = searchParams?.get('reportId');
  const { userProfile } = useAuth();
  const editorRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refining, setRefining] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [report, setReport] = useState(null);
  const [practitioner, setPractitioner] = useState(null);
  const [content, setContent] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [attachedImages, setAttachedImages] = useState([]);

  // Memoize PDF URL to prevent memory leaks
  const pdfUrl = useMemo(() => {
    if (pdfBlob && pdfBlob instanceof Blob) {
      return URL.createObjectURL(pdfBlob);
    }
    return null;
  }, [pdfBlob]);

  // Cleanup URL on unmount or when pdfBlob changes
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  useEffect(() => {
    if (reportId && userProfile?.practitionerId) {
      loadData();
    }
  }, [reportId, userProfile?.practitionerId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [reportData, practitionerData] = await Promise.all([
        endoReportService?.getById(reportId),
        practitionerService?.getById(userProfile?.practitionerId)
      ]);
      setReport(reportData);
      setContent(reportData?.content || '');
      setPractitioner(practitionerData);
      
      // Load attached images
      if (reportData?.attachments && Array.isArray(reportData?.attachments)) {
        setAttachedImages(reportData?.attachments);
      }
    } catch (error) {
      console.error('Error loading report:', error);
      toast?.error('Erreur lors du chargement du compte rendu');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await endoReportService?.update(reportId, { content });
      toast?.success('Compte rendu sauvegardé');
      // Reload to get updated version
      await loadData();
    } catch (error) {
      console.error('Error saving report:', error);
      toast?.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleRefine = async (refinementType) => {
    try {
      setRefining(true);
      const refinedContent = await endoReportService?.refineContent(reportId, content, refinementType);
      setContent(refinedContent);
      toast?.success('Contenu amélioré');
    } catch (error) {
      console.error('Error refining content:', error);
      toast?.error(error?.message || 'Erreur lors de l\'amélioration');
    } finally {
      setRefining(false);
    }
  };

  const handleGeneratePDF = async () => {
    try {
      setExporting(true);
      
      // Prepare PDF data
      const pdfData = {
        referringDentist: {
          fullName: report?.referrerName || 'Non spécifié',
          practiceName: report?.referrerPractice || '',
          email: report?.referrerEmail || '',
          phone: report?.referrerPhone || ''
        },
        patientInfo: {
          firstName: report?.patientFirstName || '',
          lastName: report?.patientLastName || '',
          yearOfBirth: report?.patientYearOfBirth || '',
          sex: '',
          phone: '',
          email: ''
        },
        toothNumbers: report?.toothNumbers || [],
        referralReasons: [],
        clinicalNotes: content || '',
        imaging: {},
        uploadedFiles: [],
        attachedImages: attachedImages || []
      };

      const doc = await generateReferralPDF(pdfData, practitioner);
      const blob = doc?.output('blob');
      setPdfBlob(blob);
      setShowPreview(true);
      
      toast?.success('Aperçu PDF généré');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast?.error('Erreur lors de la génération du PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setExporting(true);
      
      const pdfData = {
        referringDentist: {
          fullName: report?.referrerName || 'Non spécifié',
          practiceName: report?.referrerPractice || '',
          email: report?.referrerEmail || '',
          phone: report?.referrerPhone || ''
        },
        patientInfo: {
          firstName: report?.patientFirstName || '',
          lastName: report?.patientLastName || '',
          yearOfBirth: report?.patientYearOfBirth || '',
          sex: '',
          phone: '',
          email: ''
        },
        toothNumbers: report?.toothNumbers || [],
        referralReasons: [],
        clinicalNotes: content || '',
        imaging: {},
        uploadedFiles: [],
        attachedImages: attachedImages || []
      };

      const doc = await generateReferralPDF(pdfData, practitioner);
      const fileName = `CR_${report?.patientLastName}_${report?.patientFirstName}_${new Date()?.toISOString()?.split('T')?.[0]}.pdf`;
      doc?.save(fileName);
      
      toast?.success('PDF téléchargé');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast?.error('Erreur lors du téléchargement du PDF');
    } finally {
      setExporting(false);
    }
  };

  const handlePublish = async () => {
    try {
      setSaving(true);
      await endoReportService?.update(reportId, {
        content,
        status: 'final'
      });
      toast?.success('Compte rendu publié');
      navigate('/practitioner-dashboard');
    } catch (error) {
      console.error('Error publishing report:', error);
      toast?.error('Erreur lors de la publication');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A0A0F] via-[#1A1A1E] to-[#0A0A0F] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-300 text-lg">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A0A0F] via-[#1A1A1E] to-[#0A0A0F] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-300 text-lg">Compte rendu introuvable</p>
          <Button onClick={() => navigate('/practitioner-dashboard')} className="mt-4">
            Retour au tableau de bord
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Éditeur de Compte Rendu | EndoReferral</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#0A0A0F] via-[#1A1A1E] to-[#0A0A0F] py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/practitioner-dashboard')}
              className="mb-4"
            >
              <Icon name="ArrowLeft" size={20} className="mr-2" />
              Retour au tableau de bord
            </Button>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">
                  {report?.title || 'Compte rendu endodontique'}
                </h1>
                <p className="text-gray-400">
                  Patient: {report?.patientLastName} {report?.patientFirstName} | Version {report?.version}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  report?.status === 'draft' ? 'bg-yellow-500/20 text-yellow-400' :
                  report?.status === 'final'? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                }`}>
                  {report?.status === 'draft' ? 'Brouillon' : report?.status === 'final' ? 'Final' : report?.status}
                </span>
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-4 mb-6">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={handleSave}
                loading={saving}
                disabled={saving}
              >
                <Icon name="Save" size={16} className="mr-2" />
                Sauvegarder
              </Button>
              
              <div className="h-6 w-px bg-white/20"></div>
              
              <Button
                variant="outline"
                onClick={() => handleRefine('improve')}
                loading={refining}
                disabled={refining}
              >
                <Icon name="Sparkles" size={16} className="mr-2" />
                Améliorer
              </Button>
              <Button
                variant="outline"
                onClick={() => handleRefine('shorten')}
                loading={refining}
                disabled={refining}
              >
                <Icon name="Minimize2" size={16} className="mr-2" />
                Raccourcir
              </Button>
              <Button
                variant="outline"
                onClick={() => handleRefine('expand')}
                loading={refining}
                disabled={refining}
              >
                <Icon name="Maximize2" size={16} className="mr-2" />
                Détailler
              </Button>
              
              <div className="h-6 w-px bg-white/20"></div>
              
              <Button
                variant="secondary"
                onClick={handleGeneratePDF}
                loading={exporting}
                disabled={exporting}
              >
                <Icon name="FileText" size={16} className="mr-2" />
                Aperçu PDF
              </Button>
              
              <div className="ml-auto flex gap-2">
                <Button
                  variant="success"
                  onClick={handlePublish}
                  loading={saving}
                  disabled={saving || report?.status === 'final'}
                >
                  <Icon name="Check" size={16} className="mr-2" />
                  Publier
                </Button>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Editor Column */}
            <div className="lg:col-span-2">
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6">
                <h2 className="text-xl font-semibold text-white mb-4">Contenu du compte rendu</h2>
                <textarea
                  ref={editorRef}
                  value={content}
                  onChange={(e) => setContent(e?.target?.value)}
                  className="w-full h-[600px] bg-white/5 border border-white/10 rounded-xl p-4 text-white font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Contenu du compte rendu..."
                />
              </div>
              
              {/* Attached Images Section */}
              {attachedImages?.length > 0 && (
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 mt-6">
                  <h2 className="text-xl font-semibold text-white mb-4">
                    Images attachées ({attachedImages?.length})
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {attachedImages?.map((img, index) => (
                      <div
                        key={index}
                        className="relative bg-white/5 rounded-xl border border-white/10 overflow-hidden group"
                      >
                        <div className="aspect-video bg-gray-900 flex items-center justify-center">
                          <img
                            src={img?.url}
                            alt={img?.fileName || `Image ${index + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-2">
                          <p className="text-xs text-gray-400 truncate">
                            {img?.fileName || `Image ${index + 1}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            {/* Preview */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Icon name="Eye" size={20} className="text-blue-400" />
                Aperçu
              </h2>
              {showPreview && pdfUrl ? (
                <div className="space-y-4">
                  <iframe
                    src={pdfUrl}
                    className="w-full h-[600px] rounded-xl border border-white/20"
                    title="PDF Preview"
                  />
                  <Button
                    fullWidth
                    onClick={handleDownloadPDF}
                  >
                    <Icon name="Download" size={16} className="mr-2" />
                    Télécharger le PDF
                  </Button>
                </div>
              ) : (
                <div className="h-[600px] rounded-xl border border-white/20 bg-white/5 flex items-center justify-center">
                  <div className="text-center">
                    <Icon name="FileText" size={48} className="text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400 mb-4">Aucun aperçu disponible</p>
                    <Button
                      variant="outline"
                      onClick={handleGeneratePDF}
                      loading={exporting}
                    >
                      Générer l'aperçu PDF
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Report Info */}
          <div className="mt-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Informations du compte rendu</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-400">Type</p>
                <p className="text-white font-medium">{report?.reportType}</p>
              </div>
              <div>
                <p className="text-gray-400">Créé le</p>
                <p className="text-white font-medium">
                  {new Date(report?.createdAt)?.toLocaleDateString('fr-FR')}
                </p>
              </div>
              <div>
                <p className="text-gray-400">Dernière modification</p>
                <p className="text-white font-medium">
                  {new Date(report?.updatedAt)?.toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReportEditorPreview;